// Poly mid — the Position Tracker's poly column and the position/order card
// "poly" stat: the Polymarket book mid for the same market, side-native so it
// reads against the row's Avg / Now (a NO holder sees 100 − the YES-team
// value). Replaced the positions 1m Δ price chip — the Open Orders surfaces
// keep theirs.
//
// Until #1373 this was the "U/P" pair, with an Unabated consensus fair prob
// as the U half and an Unabated-vs-mark divergence pill. Core retired the
// Unabated channel on 2026-10-14 (#1191), so U was always "—" and the pill
// never fired; both are gone. Do not refill the U slot with OddsPapi or any
// computed consensus: #1192/#1373 forbid substituting another signal.
//
// Values are computed by PositionTracker's coarse per-second sweep (same
// clock as the divergence badge): bookMid on the paired game slot's book.
// That sweep's refs also feed the Tracker's feed filter and poly orderbook
// subscriptions, so it outlives any change to this display. A poly book with
// no recent orderbook frame renders muted — a frozen feed must not read as a
// live price ("Stale prices mask dead hydra feeds", CLAUDE.md).

import { POLY_STALE_MS } from '@/lib/divergence/polyKalshiDivergence';

/** Tracker column header for the poly mid (#1373; Nadya's label call). */
export const POLY_MID_COLUMN_LABEL = 'Poly';
/** Card stat label for the poly mid — cards use lowercase stat labels. */
export const POLY_MID_STAT_LABEL = 'poly';

export interface UPOdds {
  /** Side-native Polymarket book mid, cents. Null when the game has no
   *  paired poly slot or the book is one-sided. */
  pCents: number | null;
  /** Poly orderbook recent enough to trust (same POLY_STALE_MS gate as the
   *  divergence badge). False renders the value muted. */
  polyFresh: boolean;
}

/** YES-team probability → the holder's side. NO backs the complement. */
export function sideNativeProbCents(
  yesCents: number | null,
  side: 'yes' | 'no',
): number | null {
  if (yesCents == null) return null;
  return side === 'yes' ? yesCents : 100 - yesCents;
}

/** The side-native poly mid — shared by the table cell and the card StatLine
 *  so the two views can't drift. "—" when unresolved (non-moneyline markets,
 *  unpaired games, one-sided books). */
export function UPOddsValue({ odds }: { odds: UPOdds | null | undefined }) {
  // A BOOK MID — (bid+ask)/2 — so a 1¢-wide book yields an x.5 that
  // Polymarket never actually quotes. Display it as an integer (round the
  // already-side-native value; rounding before the NO flip would disagree
  // with the YES read by 1¢ on exact halves).
  const p = odds?.pCents != null ? Math.round(odds.pCents) : null;
  const polyStale = p != null && !odds?.polyFresh;
  return (
    <span
      className={`whitespace-nowrap ${p != null && !polyStale ? 'text-foreground' : 'text-muted-foreground'}`}
      title={
        polyStale
          ? `Polymarket mid (side-native). Book stale: no orderbook frame in ${Math.round(POLY_STALE_MS / 60_000)}+ min`
          : 'Polymarket book mid (side-native)'
      }
    >
      {p == null ? '—' : String(p)}
    </span>
  );
}
